/**
 * Accordion header placeholder helpers.
 *
 * The Accordion "Title Text" used to SEED the literal string "Accordion Header"
 * as the block's actual header content. That looked like placeholder text but
 * was real content: typing did not clear it, so the user had to backspace it
 * letter-by-letter. The fix seeds an EMPTY header and surfaces the hint two ways:
 *   1. the editor's real Froala `placeholderText` (greyed, vanishes on input,
 *      never saved) — wired in AccordionSettings.
 *   2. a canvas-only greyed hint in the builder preview so a fresh accordion is
 *      not a blank bar — via getAccordionDisplayHeader below.
 *
 * The canvas hint is shown ONLY in the editable builder (craftjs `enabled`), so
 * it never leaks to the read-only published wheel (rendered with
 * <Editor enabled={false}>), and it is display-only — the saved `header` prop is
 * never mutated.
 */

export const ACCORDION_HEADER_PLACEHOLDER_TEXT = "Accordion Header";

// Muted (opacity) so it reads as a hint over the white header title. `span` +
// inline `style` are both preserved by sanitizeUserHtml's allow-list.
const PLACEHOLDER_HTML = `<span style="opacity:0.6">${ACCORDION_HEADER_PLACEHOLDER_TEXT}</span>`;

/**
 * True when the header has no visible text (null/undefined, whitespace only, or
 * empty formatting tags like "<p><br></p>").
 * @param {string} header
 * @returns {boolean}
 */
export function isAccordionHeaderEmpty(header) {
  if (header == null) return true;
  const text = String(header)
    .replace(/<[^>]*>/g, "") // strip tags
    .replace(/&nbsp;/gi, " ") // html nbsp entity
    .replace(/[ ​]/g, " ") // nbsp / zero-width space chars
    .trim();
  return text.length === 0;
}

/**
 * Header HTML to DISPLAY in the accordion preview.
 * Returns the greyed placeholder ONLY in the editable builder when the real
 * header is empty; otherwise returns the real header verbatim (saved content and
 * the live wheel are untouched).
 * @param {string} header - the saved header content
 * @param {boolean} enabled - craftjs edit mode (true = builder, false = view)
 * @returns {string}
 */
export function getAccordionDisplayHeader(header, enabled) {
  if (enabled && isAccordionHeaderEmpty(header)) return PLACEHOLDER_HTML;
  return header;
}
